import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";
import { format } from "date-fns";
import PageHeader from "@/components/PageHeader";
import DatePicker from "@/components/DatePicker";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function BookingConfirmPage() {
  useSeo({ title: "Confirm Your Booking | Cool Runnings Cape Town", noindex: true });
  const [form, setForm] = useState({ email: "", date: null, website: "" });
  const [sending, setSending] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const confirm = async () => {
    if (sending) return;
    setSending(true);
    try {
      const res = await api.post("/booking/confirm", {
        email: form.email,
        date: form.date ? format(form.date, "yyyy-MM-dd") : "",
        website: form.website,
      });
      setConfirmed({ ref: res.data.ref, date: form.date, email: form.email });
      window.scrollTo(0, 0);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(
        typeof d === "string"
          ? d
          : d?.[0]?.msg?.replace("Value error, ", "") || "Could not confirm — please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHeader
        goodgirlTitle
        pageName="Booking confirmation"
        eyebrow="Party packs & events"
        title="CONFIRM YOUR BOOKING"
        subline="Already booked your party with us? Confirm your date here, then email us your party packs and kids feasts — 2 days' notice."
        image="/park-track.jpg"
      />

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6" data-testid="booking-confirm-page">
        {confirmed ? (
          <div className="card-dark flex flex-col items-center gap-4 px-8 py-14 text-center" data-testid="booking-confirm-success">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500/15 text-green-400">
              <CheckCircle2 size={40} />
            </span>
            <p className="font-display text-3xl">Your visit is confirmed!</p>
            <p className="max-w-md text-sm leading-relaxed text-[#b9b4a6]">
              We have your <b className="text-[#f8b818]">{confirmed.ref}</b> confirmation for{" "}
              <b className="text-[#f8b818]">{format(confirmed.date, "EEEE, d MMMM yyyy")}</b> — a copy is on its way
              to {confirmed.email}. Party packs are now up for grabs!
            </p>
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              <Link to="/menu" className="btn-gold !px-6 !py-2.5 !text-xs" data-testid="booking-confirm-shop-btn">
                Shop party packs
              </Link>
              <Link to="/" className="btn-outline !px-6 !py-2.5 !text-xs" data-testid="booking-confirm-home-btn">
                Back home
              </Link>
            </div>
          </div>
        ) : (
          <div className="card-dark p-6 sm:p-10" data-testid="booking-confirm-form-card">
            <div className="space-y-4">
              <input
                type="email"
                className="w-full rounded-2xl border border-[#241c0d]/20 bg-white px-4 py-3 text-sm font-semibold text-[#241c0d] outline-none transition-colors placeholder:font-normal placeholder:text-[#8a7b5e] focus:border-[#f8b818]"
                placeholder="The email you booked with *"
                required
                maxLength={120}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="booking-confirm-email-input"
              />
              <DatePicker
                value={form.date}
                onChange={(d) => setForm({ ...form, date: d })}
                placeholder="Your event date *"
                testid="booking-confirm-date"
              />
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                data-testid="booking-confirm-website-hp"
              />
              <button
                onClick={confirm}
                disabled={sending || !form.email.trim() || !form.date}
                className="btn-gold w-full justify-center !text-white shadow-[0_10px_30px_rgba(248,184,24,0.45)] disabled:!bg-[#6b5300] disabled:!text-white disabled:!opacity-100 disabled:!shadow-none disabled:cursor-not-allowed"
                data-testid="booking-confirm-btn"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={15} />}
                {sending ? "Confirming…" : "Confirm my booking"}
              </button>
              <p className="text-center text-[11px] leading-relaxed text-[#8a7b5e]">
                Booked through the website or by email with Janet? Confirm your date here and party pack combos
                unlock for the big day.
              </p>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
